/**
 * Diagramme.
 *
 * Bewusst ohne Diagrammbibliothek: Die benötigten Darstellungen sind einfache
 * Linien-, Flächen- und Balkenformen. Handgeschriebenes SVG spart eine große
 * Abhängigkeit, rendert schneller und lässt sich exakt an das Farbschema binden.
 */
import { useId, useMemo } from 'react';
import clsx from 'clsx';

export interface Series {
  label: string;
  points: number[];
  color?: string;
}

const DEFAULT_COLORS = ['#21a471', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6'];

function extent(all: number[][]): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const points of all) {
    for (const value of points) {
      if (!Number.isFinite(value)) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };
  if (min === max) return { min: min - 1, max: max + 1 };
  // Etwas Luft nach oben, damit die Spitze nicht am Rand klebt.
  return { min: Math.min(0, min), max: max + (max - min) * 0.1 };
}

function buildPath(points: number[], width: number, height: number, min: number, max: number): string {
  if (points.length === 0) return '';
  const span = max - min || 1;
  const step = points.length > 1 ? width / (points.length - 1) : width;
  return points
    .map((value, index) => {
      const x = index * step;
      const y = height - ((value - min) / span) * height;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)},${Number.isFinite(y) ? y.toFixed(2) : height}`;
    })
    .join(' ');
}

/** Kleine Verlaufskurve ohne Achsen – für Kennzahlenkacheln. */
export function Sparkline({ points, color = '#21a471', height = 32 }: { points: number[]; color?: string; height?: number }) {
  const width = 120;
  const { min, max } = extent([points]);
  const path = buildPath(points, width, height, min, max);
  const id = useId();

  if (points.length < 2) return <div style={{ height }} className="text-2xs text-panel-400">Noch keine Daten</div>;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }} preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L${width},${height} L0,${height} Z`} fill={`url(#spark-${id})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** Liniendiagramm mit Achsen, Raster und Legende. */
export function LineChart({
  series,
  height = 220,
  unit,
  formatValue,
  className,
}: {
  series: Series[];
  height?: number;
  unit?: string;
  formatValue?: (value: number) => string;
  className?: string;
}) {
  const width = 640;
  const padding = { top: 8, right: 8, bottom: 20, left: 48 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  const { min, max } = useMemo(() => extent(series.map((s) => s.points)), [series]);
  const format = formatValue ?? ((v: number) => new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(v));

  // Fünf waagerechte Hilfslinien reichen zur Orientierung, ohne zu überladen.
  const gridLines = Array.from({ length: 5 }, (_, i) => min + ((max - min) * i) / 4);
  const hasData = series.some((s) => s.points.length > 1);

  return (
    <div className={className}>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }} role="img">
        <g transform={`translate(${padding.left},${padding.top})`}>
          {gridLines.map((value, index) => {
            const y = plotHeight - ((value - min) / (max - min || 1)) * plotHeight;
            return (
              <g key={index}>
                <line x1={0} x2={plotWidth} y1={y} y2={y} className="stroke-panel-200 dark:stroke-panel-800" strokeWidth="1" />
                <text x={-8} y={y + 3} textAnchor="end" className="fill-panel-400 text-[9px] dark:fill-panel-500">
                  {format(value)}
                </text>
              </g>
            );
          })}

          {hasData &&
            series.map((s, index) => (
              <path
                key={s.label}
                d={buildPath(s.points, plotWidth, plotHeight, min, max)}
                fill="none"
                stroke={s.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                strokeWidth="1.8"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            ))}

          {!hasData && (
            <text x={plotWidth / 2} y={plotHeight / 2} textAnchor="middle" className="fill-panel-400 text-[11px]">
              Noch keine Messwerte – die Simulation muss dafür laufen.
            </text>
          )}
        </g>
      </svg>

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {series.map((s, index) => (
          <span key={s.label} className="inline-flex items-center gap-1.5 text-xs text-panel-600 dark:text-panel-300">
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: s.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length] }}
            />
            {s.label}
            {s.points.length > 0 && (
              <span className="tabular text-panel-400 dark:text-panel-500">
                {format(s.points[s.points.length - 1])}
                {unit ? ` ${unit}` : ''}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Waagerechtes Balkendiagramm – für Kostenaufschlüsselungen. */
export function BarBreakdown({
  items,
  formatValue,
  emptyLabel = 'Keine Daten',
}: {
  items: { label: string; value: number; color?: string }[];
  formatValue: (value: number) => string;
  emptyLabel?: string;
}) {
  const max = Math.max(...items.map((i) => Math.abs(i.value)), 1);
  if (items.length === 0) return <p className="py-6 text-center text-xs text-panel-400">{emptyLabel}</p>;

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={item.label}>
          <div className="mb-0.5 flex justify-between gap-2 text-xs">
            <span className="truncate text-panel-600 dark:text-panel-300">{item.label}</span>
            <span className="tabular shrink-0 font-medium">{formatValue(item.value)}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-panel-200 dark:bg-panel-800">
            <div
              className="h-full rounded-full"
              style={{
                width: `${(Math.abs(item.value) / max) * 100}%`,
                background: item.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length],
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Ringdiagramm für Anteile (z. B. Transit gegen Peering). */
export function DonutChart({
  segments,
  size = 132,
  centerLabel,
  centerValue,
}: {
  segments: { label: string; value: number; color?: string }[];
  size?: number;
  centerLabel?: string;
  centerValue?: string;
}) {
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  const radius = size / 2 - 12;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" className="stroke-panel-200 dark:stroke-panel-800" strokeWidth="12" />
          {total > 0 &&
            segments.map((segment, index) => {
              const fraction = Math.max(0, segment.value) / total;
              const dash = fraction * circumference;
              const element = (
                <circle
                  key={segment.label}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={segment.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                  strokeWidth="12"
                  strokeDasharray={`${dash} ${circumference - dash}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += dash;
              return element;
            })}
        </g>
        {(centerValue || centerLabel) && (
          <>
            <text x="50%" y="47%" textAnchor="middle" className="fill-panel-900 text-sm font-semibold dark:fill-panel-50">
              {centerValue}
            </text>
            <text x="50%" y="60%" textAnchor="middle" className="fill-panel-400 text-[9px] uppercase tracking-wide">
              {centerLabel}
            </text>
          </>
        )}
      </svg>
      <ul className="min-w-0 flex-1 space-y-1">
        {segments.map((segment, index) => (
          <li key={segment.label} className="flex items-center justify-between gap-2 text-xs">
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: segment.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length] }}
              />
              <span className="truncate text-panel-600 dark:text-panel-300">{segment.label}</span>
            </span>
            <span className="tabular shrink-0 font-medium">
              {total > 0 ? `${((segment.value / total) * 100).toFixed(0)} %` : '–'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export { DEFAULT_COLORS };
export const chartClass = clsx;
